import React from 'react'

import { Formik } from 'formik'
import { Field } from 'formik'
import { useHistory } from 'react-router-dom'

import Yup from '_zari/validation/_yup'

import { ZariContext } from '_zari/context'
import { Button, Col, Form, Portlet, Row } from '_zari/core'

import { CodigosTransaccionesConstant } from 'constants/index'

import { IdGen } from 'utils'

import * as AlmacenCrud from 'services/almacen.service'
import * as SedeCrud from 'services/sede.service'

const ValidationSchema = Yup.object().shape({
  _idSede: Yup.string().required(),
  nombre: Yup.string().max(100).required(),
  descripcion: Yup.string().max(200)
})

const NuevoAlmacenPage = ({ match }) => {
  const __zariContext = React.useContext(ZariContext)
  const history = useHistory()

  const [__keyMainForm, setKeyMainForm] = React.useState(null)
  const [sedes, setSedes] = React.useState([])

  const editarDataRef = React.useRef({})

  const portletBreadcrumbs = React.useMemo(() => {
    if (match.params.id) {
      return [{ name: 'Almacenes', path: `/${CodigosTransaccionesConstant.ALMACENES_LISTA}` }, { name: 'Editar' }]
    }
    return [{ name: 'Almacenes', path: `/${CodigosTransaccionesConstant.ALMACENES_LISTA}` }, { name: 'Nuevo' }]
  }, [match.params.id])

  React.useEffect(() => {
    SedeCrud.obtenerAsSelect().then(({ data }) => setSedes(data))
  }, [])

  React.useEffect(() => {
    if (match.params.id) {
      AlmacenCrud.getAlmacen(match.params.id)
        .then(({ data }) => {
          editarDataRef.current = data
          setKeyMainForm(IdGen.newId())
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    } else {
      setKeyMainForm(IdGen.newId())
    }
  }, [match.params, __zariContext])

  const formInitialValues = React.useMemo(
    () => ({
      _idSede: editarDataRef.current._idSede ?? '',
      nombre: editarDataRef.current.nombre ?? '',
      descripcion: editarDataRef.current.descripcion ?? ''
    }),
    [__keyMainForm]
  )

  const submitHandler = React.useCallback(
    async data => {
      if (match.params.id) {
        await AlmacenCrud.actualizarAlmacen(match.params.id, data)
          .then(() => {
            __zariContext.snack('success', 'El almacen se ha actualizado.')
            history.push(`/${CodigosTransaccionesConstant.ALMACENES_LISTA}`)
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      } else {
        await AlmacenCrud.nuevoAlmacen(data)
          .then(() => {
            __zariContext.snack('success', 'El almacen se ha creado.')
            history.push(`/${CodigosTransaccionesConstant.ALMACENES_LISTA}`)
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      }
    },
    [__zariContext, history, match.params.id]
  )

  return (
    <Portlet>
      <Portlet.Header title='Almacenes' breadcrumbs={portletBreadcrumbs} />
      <Portlet.Body>
        {__keyMainForm && (
          <Formik key={__keyMainForm} initialValues={formInitialValues} onSubmit={submitHandler} validationSchema={ValidationSchema}>
            {({ errors, handleSubmit, isSubmitting, touched }) => (
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label text='SEDE' htmlFor='select-sede' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='_idSede'>
                          {({ field }) => (
                            <Form.Select {...field} disabled={isSubmitting} isInvalid={touched._idSede && errors._idSede} id='select-sede'>
                              <option value=''>Seleccionar</option>
                              {sedes &&
                                sedes.map((val, key) => (
                                  <option key={key} value={val._id}>
                                    {val.nombre}
                                  </option>
                                ))}
                            </Form.Select>
                          )}
                        </Field>
                        {touched._idSede && errors._idSede ? <Form.Feedback type='invalid'>{errors._idSede}</Form.Feedback> : null}
                      </Col>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label text='NOMBRE' htmlFor='input-nombre' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='nombre'>
                          {({ field }) => (
                            <Form.InputText {...field} isInvalid={touched.nombre && errors.nombre} disabled={isSubmitting} id='input-nombre' />
                          )}
                        </Field>
                        {touched.nombre && errors.nombre ? <Form.Feedback type='invalid'>{errors.nombre}</Form.Feedback> : null}
                      </Col>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label text='DESCRIPCION' htmlFor='input-descripcion' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='descripcion'>
                          {({ field }) => (
                            <Form.InputText
                              {...field}
                              isInvalid={touched.descripcion && errors.descripcion}
                              disabled={isSubmitting}
                              id='input-descripcion'
                            />
                          )}
                        </Field>
                        {touched.descripcion && errors.descripcion ? (
                          <Form.Feedback type='invalid'>{errors.descripcion}</Form.Feedback>
                        ) : null}
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className='text-right'>
                    <Button color='secondary' onClick={() => history.push(`/${CodigosTransaccionesConstant.ALMACENES_LISTA}`)}>
                      Cancelar
                    </Button>{' '}
                    <Button onClick={handleSubmit}>{match.params.id ? 'Actualizar' : 'Crear'}</Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}
      </Portlet.Body>
    </Portlet>
  )
}

export default React.memo(NuevoAlmacenPage)
